import React, { forwardRef, useEffect, useState } from "react";

// Drop-in replacement for <img> on config-driven guide images.
//
// Those images are remote URLs from the persona config.json in S3, fetched at
// render time. A plain <img> with no onError leaves WebKit's broken-image glyph
// inside the bordered .zero-item tile when the fetch fails - that is the "blue
// boxed question mark" drivers reported. Empty src is worse: it resolves to the
// document URL, returns HTML, and breaks every single time.
//
// One retry with a cache buster covers the weak-signal case (drivers are in
// yards and depots), then it settles on a placeholder that cannot itself fail.

const PLACEHOLDER =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">' +
      '<rect x="8" y="26" width="84" height="54" rx="8" fill="none" stroke="#9aa0a6" stroke-width="4"/>' +
      '<path d="M34 26l6-10h20l6 10" fill="none" stroke="#9aa0a6" stroke-width="4" stroke-linejoin="round"/>' +
      '<circle cx="50" cy="54" r="15" fill="none" stroke="#9aa0a6" stroke-width="4"/>' +
      "</svg>",
  );

// Same cache-busting shape already used before drawing a stencil onto canvas
// in MultipleImage.js.
const withCacheBuster = (src) =>
  src + (src.includes("?") ? "&" : "?") + "cb=" + new Date().getTime();

const SafeImage = forwardRef(function SafeImage(
  { src, alt = "", fallback = PLACEHOLDER, ...rest },
  ref,
) {
  const [current, setCurrent] = useState(src || fallback);
  const [retried, setRetried] = useState(false);

  useEffect(() => {
    setCurrent(src || fallback);
    setRetried(false);
  }, [src, fallback]);

  const handleError = () => {
    if (src && !retried) {
      setRetried(true);
      setCurrent(withCacheBuster(src));
    } else if (current !== fallback) {
      setCurrent(fallback);
    }
    // Once we are already showing the fallback there is nothing left to try,
    // so no further state change - this is what stops an onError loop.
  };

  // fallback={null} means "render nothing rather than a placeholder". Use it
  // for overlays and decoration: a grey placeholder tile sitting on top of the
  // live camera preview would be worse for the driver than no stencil at all.
  if (!current) return null;

  return (
    <img
      {...rest}
      ref={ref}
      src={current}
      alt={alt}
      onError={handleError}
    />
  );
});

export default SafeImage;
